import { Link } from "react-router-dom";
import { useTranslation } from "../../i18n";

const VALUE_ICONS = [
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
  </>,
  <>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
  </>,
  <path d="M12 2 2 7l10 5 10-5-10-5ZM2 17l10 5 10-5M2 12l10 5 10-5" />,
];

export default function NewTeamRoute() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <main>
        {/* ── Page hero ────────────────────────────────────── */}
        <section className="relative px-4 md:px-8 pt-32 pb-[clamp(2.5rem,5vw,4rem)] text-center">
          <div className="pointer-events-none absolute -top-28 left-1/2 h-[440px] w-[440px] -translate-x-1/2 rounded-full bg-juhuu-glow blur-[90px]" />
          <div className="relative mx-auto max-w-7xl">
            <p className="text-[.78rem] font-bold uppercase tracking-[.16em] text-juhuu">
              {t("routes.team.kicker")}
            </p>
            <h1 className="mt-4 text-[clamp(2.4rem,4.6vw,3.8rem)] font-extrabold leading-[1.02] tracking-[-0.035em] text-ink">
              {t("routes.team.heroTitleLine1")}
              <br />
              {t("routes.team.heroTitleLine2")}
            </h1>
            <p className="mx-auto mt-5 max-w-[620px] text-[clamp(1.05rem,0.7vw+0.95rem,1.2rem)] font-medium leading-[1.65] text-muted">
              {t("routes.team.heroDescription")}
            </p>
          </div>
        </section>

        {/* ── Mission ──────────────────────────────────────── */}
        <section className="px-4 md:px-8 pb-14">
          <div className="mx-auto max-w-7xl">
            <div className="rounded-2xl border border-juhuu-soft-2 bg-gradient-to-br from-juhuu-soft to-white p-[clamp(1.7rem,3vw,2.6rem)]">
              <div className="grid items-center gap-10 lg:grid-cols-2">
                <div>
                  <p className="text-[.78rem] font-bold uppercase tracking-[.16em] text-juhuu">
                    {t("routes.team.missionKicker")}
                  </p>
                  <h2 className="mt-3 text-[clamp(1.5rem,2.3vw,2.1rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
                    {t("routes.team.missionTitle")}
                  </h2>
                </div>
                <p className="text-[1.02rem] leading-[1.75] text-muted">
                  {t("routes.team.missionText")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Values ───────────────────────────────────────── */}
        <section className="px-4 md:px-8 py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10">
              <p className="text-[.78rem] font-bold uppercase tracking-[.16em] text-muted">
                {t("routes.team.valuesKicker")}
              </p>
              <h2 className="mt-4 max-w-[520px] text-[clamp(1.8rem,3vw,2.6rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
                {t("routes.team.valuesTitle")}
              </h2>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-line bg-white p-7 shadow-sm"
                >
                  <div className="mb-4 grid h-[46px] w-[46px] place-items-center rounded-[13px] border border-juhuu-soft-2 bg-juhuu-soft text-juhuu">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="h-[22px] w-[22px]"
                    >
                      {VALUE_ICONS[i]}
                    </svg>
                  </div>
                  <h3 className="text-[1.12rem] font-bold text-ink">
                    {t(`routes.team.values.${i}.title`)}
                  </h3>
                  <p className="mt-2 text-[.9rem] leading-[1.6] text-muted">
                    {t(`routes.team.values.${i}.description`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Award strip ──────────────────────────────────── */}
        <section className="px-4 md:px-8 pb-14">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-wrap items-center justify-between gap-6 rounded-2xl border border-line bg-white p-[clamp(1.7rem,3vw,2.6rem)] shadow-sm">
              <div className="flex items-center gap-5">
                <div className="grid h-[60px] w-[60px] flex-shrink-0 place-items-center rounded-full bg-juhuu-soft text-juhuu">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    className="h-[30px] w-[30px]"
                  >
                    <circle cx="12" cy="8" r="6" />
                    <path d="M8.5 13 7 22l5-3 5 3-1.5-9" />
                  </svg>
                </div>
                <div>
                  <div className="text-[1.1rem] font-extrabold text-ink">
                    {t("routes.team.awardTitle")}
                  </div>
                  <div className="text-[.9rem] text-muted">
                    {t("routes.team.awardSubtitle")}
                  </div>
                </div>
              </div>
              <p className="max-w-[380px] text-[.92rem] leading-[1.6] text-muted">
                {t("routes.team.awardText")}
              </p>
            </div>
          </div>
        </section>

        {/* ── Team ─────────────────────────────────────────── */}
        <section className="px-4 md:px-8 py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto mb-10 max-w-[560px] text-center">
              <p className="text-[.78rem] font-bold uppercase tracking-[.16em] text-juhuu">
                {t("routes.team.teamKicker")}
              </p>
              <h2 className="mt-4 text-[clamp(1.8rem,3vw,2.6rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
                {t("routes.team.teamTitle")}
              </h2>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-line bg-white px-5 py-6 text-center shadow-sm"
                >
                  <div className="mx-auto mb-4 grid h-[84px] w-[84px] place-items-center rounded-full bg-gradient-to-br from-juhuu to-[#00b4ff] text-[1.4rem] font-extrabold text-white">
                    {t(`routes.team.members.${i}.initials`)}
                  </div>
                  <div className="font-extrabold text-ink">
                    {t(`routes.team.members.${i}.name`)}
                  </div>
                  <div className="mt-1 text-[.85rem] text-muted">
                    {t(`routes.team.members.${i}.role`)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Location ─────────────────────────────────────── */}
        <section className="bg-bg-alt px-4 md:px-8 py-20">
          <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
            <div>
              <p className="text-[.78rem] font-bold uppercase tracking-[.16em] text-juhuu">
                {t("routes.team.locationKicker")}
              </p>
              <h2 className="mt-3 text-[clamp(1.6rem,2.5vw,2.3rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
                {t("routes.team.locationTitleLine1")}
                <br />
                {t("routes.team.locationTitleLine2")}
              </h2>
              <p className="my-5 leading-[1.65] text-muted">
                {t("routes.team.locationText")}
              </p>
              <Link
                to="/meeting"
                className="inline-flex items-center gap-1.5 rounded-full bg-juhuu-soft px-6 py-3 font-semibold text-juhuu-deep transition-colors hover:bg-juhuu-soft-2"
              >
                {t("routes.team.locationCta")}
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4"
                >
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </Link>
            </div>

            {/* Stylised map — grid lines, glow and a pin (CSS only, as designed) */}
            <div
              className="relative h-[280px] overflow-hidden rounded-2xl border border-line"
              style={{
                background:
                  "radial-gradient(circle at 60% 40%, rgba(109,22,250,.12), transparent 50%), repeating-linear-gradient(0deg,#eee9f7 0 1px,transparent 1px 40px), repeating-linear-gradient(90deg,#eee9f7 0 1px,transparent 1px 40px),#fbfaff",
              }}
            >
              <span
                className="absolute h-10 w-10 bg-juhuu shadow-[0_14px_40px_rgba(109,22,250,0.28)]"
                style={{
                  top: "42%",
                  left: "58%",
                  borderRadius: "50% 50% 50% 0",
                  transform: "rotate(-45deg)",
                }}
              />
              <span className="absolute bottom-3.5 left-3.5 rounded-[10px] border border-line bg-white px-3 py-2 text-[.85rem] font-bold text-ink">
                {t("routes.team.locationBadge")}
              </span>
            </div>
          </div>
        </section>

        {/* ── Closing CTA ──────────────────────────────────── */}
        <section className="px-4 md:px-8 py-20 text-center">
          <div className="mx-auto max-w-[620px]">
            <h2 className="text-[clamp(1.8rem,3vw,2.6rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
              {t("routes.team.finalCtaTitle")}
            </h2>
            <p className="mx-auto my-6 max-w-[460px] text-[clamp(1.05rem,0.7vw+0.95rem,1.2rem)] font-medium leading-[1.65] text-muted">
              {t("routes.team.finalCtaDescription")}
            </p>
            <Link
              to="/meeting"
              className="inline-flex items-center rounded-full bg-juhuu px-7 py-3.5 font-semibold text-white shadow-[0_14px_30px_-10px_rgba(109,22,250,.55)] transition-all hover:-translate-y-0.5 hover:bg-juhuu-ink"
            >
              {t("routes.team.finalCtaButton")}
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
